<template>
  <section v-if="Object.keys(details.carInfo).length">
    <details-top-bar
      :title="generateName"
      :description="details.carInfo.trim"
      :car-name="generateName"
      :car-image-url="details.carInfo.images[0]"
    />
    <div class="details__main">
      <base-image-slider
        :images="details.carInfo.images"
        :monthly-price="details.carInfo.monthly_hp"
        :car-price="details.carInfo.price"
      />
      <div class="main-info">
        <div>
          <base-price-badges
            class="main-info__price-badges"
            :monthly-price="details.carInfo.monthly_hp"
            :car-price="details.carInfo.price"
            full-width
          />
          <h1 class="main-info__description">Main Information</h1>
          <base-car-details
            v-if="details.carInfo.seller"
            :name="generateName"
            :gearbox="$store.getters.decodeGearboxCode(details.carInfo.gearbox)"
            :fuel="details.carInfo.fuel"
            :mileage="generateMileage"
            :body="details.carInfo.body"
            :engine-size="details.carInfo.enginesize"
            :model="details.carInfo.model"
            :trim="details.carInfo.trim"
            :year="details.carInfo.year"
            :doors="details.carInfo.numdoors"
            :dealer="details.carInfo.seller.name"
            :phone="details.carInfo.seller.contact_phone"
            desktop-extended
          />
        </div>
        <details-navigation-buttons
          :test-drive-id="testDriveFormId"
          :finance-id="financeQuoteId"
        />
      </div>
    </div>
    <div class="details-wrapper">
      <details-car-description
        :trim="details.carInfo.trim"
        :options="details.carInfo.options"
        :description="details.carInfo.description"
      />
      <details-dealer-location
        v-if="details.carInfo.seller"
        :name="details.carInfo.seller.name"
        :address="details.carInfo.seller.address1"
        :phone="details.carInfo.seller.contact_phone"
        :email="details.carInfo.seller.contact_email"
        :town="details.carInfo.seller.town"
        :latitude="details.carInfo.seller.latitude"
        :longitude="details.carInfo.seller.longitude"
      />
      <test-drive-form
        v-if="details.carInfo.pk"
        :car-id="details.carInfo.pk"
        :form-id="testDriveFormId"
      />
      <finance-quote
        v-if="details.carInfo.pk"
        :car-id="details.carInfo.pk"
        :car-price="details.carInfo.price"
        :monthly-price="details.carInfo.monthly_hp"
        :form-id="financeQuoteId"
        :car-year="details.carInfo.year"
      />
    </div>
  </section>
  <div v-else class="loading">
    <base-spinner />
  </div>
</template>

<script>
import DetailsTopBar from "@/components/details/DetailsTopBar/DetailsTopBar.vue";
import BaseImageSlider from "@/components/ui/BaseImageSlider/BaseImageSlider.vue";
import BasePriceBadges from "@/components/ui/BasePriceBadges/BasePriceBadges.vue";
import BaseCarDetails from "@/components/ui/BaseCarDetails/BaseCarDetails.vue";
import BaseSpinner from "@/components/ui/BaseSpinner/BaseSpinner.vue";
import DetailsNavigationButtons from "@/components/details/DetailsNavigationButtons/DetailsNavigationButtons.vue";
import DetailsCarDescription from "@/components/details/DetailsCarDescription/DetailsCarDescription.vue";
import DetailsDealerLocation from "@/components/details/DetailsDealerLocation/DetailsDealerLocation.vue";
import TestDriveForm from "@/components/details/TestDriveForm/TestDriveForm.vue";
import FinanceQuote from "@/components/details/FinanceQuote/FinanceQuote.vue";
import { addSpacesToNumber } from "@/utils";
import { GET_INFO_ABOUT_CAR } from "@/store/actionTypes";
import { mapState } from "vuex";

export default {
  name: "Details",
  metaInfo() {
    return {
      title: this.$t("meta-info.title.details", {
        carName: this.generateName
      })
    };
  },
  components: {
    DetailsTopBar,
    BaseImageSlider,
    BasePriceBadges,
    BaseCarDetails,
    BaseSpinner,
    DetailsNavigationButtons,
    DetailsCarDescription,
    DetailsDealerLocation,
    TestDriveForm,
    FinanceQuote
  },
  computed: {
    ...mapState(["details"]),
    generateName() {
      return `${this.details.carInfo.year} ${this.details.carInfo.make} ${
        this.details.carInfo.model
      } ${this.details.carInfo.body}`;
    },
    generateMileage() {
      return `
        ${addSpacesToNumber(this.details.carInfo.mileage)}
        ${this.details.carInfo.mileageunits}
      `;
    },
    testDriveFormId() {
      return "test-drive-form";
    },
    financeQuoteId() {
      return "finance-quote";
    }
  },
  async created() {
    try {
      await this.$store.dispatch(GET_INFO_ABOUT_CAR, this.$route.params.carId);
    } catch {
      return this.$router.push("/not-found");
    }
  }
};
</script>

<style lang="scss" scoped>
.details-wrapper {
  padding: 25px 20px;
}

.main-info {
  padding: 25px 20px 0;

  &__price-badges {
    display: none;
  }

  &__description {
    display: none;
  }
}

@media screen and (min-width: $media-l) {
  .details__main {
    @include grid-columns(2);
    @include grid-ie11-fix(2);
  }

  .details-wrapper {
    padding: 0 25px;
  }

  .main-info {
    padding: 0 30px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    &__price-badges {
      display: flex;
    }

    &__description {
      display: block;
      color: $color-blue-300;
      border-bottom: 1px solid $color-grey-100;
      margin: 0 0 25px 0;
      padding: 25px 0;
    }
  }
}

@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
  .main-info {
    max-width: 43% !important;
  }
}

@media screen and (min-width: $media-xxxl) {
  .main-info {
    justify-content: flex-start;
  }
}
</style>
